import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { AuthPointItem, IStaff } from '@types'
import { AuthPointType } from '@configs/auth-point'

// ========== 类型定义 ==========
interface SelectDataType {
  [key: string]: any[]
}

export type AuthPointMapType = Record<AuthPointType, AuthPointItem>

interface GlobalState {
  // 状态
  selectorData: SelectDataType
  authPointMap: AuthPointMapType
  staffData: { [key: string]: IStaff }

  // setter 方法 - 兼容 useObject 的所有调用方式
  setSelectorData: {
    (key: string, value: any): void
    (data: Partial<SelectDataType>): void
    (fn: (draft: SelectDataType) => void): void
  }
  setAuthPointMap: {
    (key: AuthPointType, value: AuthPointItem): void
    (data: Partial<AuthPointMapType>): void
    (fn: (draft: AuthPointMapType) => void): void
  }
  setStaffData: {
    (key: string, value: IStaff): void
    (data: Partial<{ [key: string]: IStaff }>): void
    (fn: (draft: { [key: string]: IStaff }) => void): void
  }

  // 重置方法
  reset: () => void
}

// ========== 初始状态 ==========
const initialState = {
  selectorData: {} as SelectDataType,
  authPointMap: {} as AuthPointMapType,
  staffData: {} as { [key: string]: IStaff },
}

// ========== 创建 Store ==========
export const globalStore = create<GlobalState>()(
  immer((set) => ({
    ...initialState,

    // ========== Setter 方法 - 完全兼容 useObject API ==========
    /**
     * 设置公共选择器数据
     * 支持 3 种调用方式：
     * 1. setSelectorData('key', value) - 设置单个属性
     * 2. setSelectorData({ key: value }) - 浅合并对象
     * 3. setSelectorData(draft => { draft.key = value }) - 函数更新
     */
    setSelectorData: (data: any, value?: any) => {
      set((state) => {
        if (typeof data === 'string') {
          // 单个属性设置
          state.selectorData[data] = value
        } else if (typeof data === 'function') {
          // 函数方式
          data(state.selectorData)
        } else if (typeof data === 'object' && data !== null) {
          // 对象方式 - 浅合并
          Object.assign(state.selectorData, data)
        }
      })
    },

    /**
     * 设置权限数据
     * 支持 3 种调用方式：
     * 1. setAuthPointMap('OPPORTUNITY_LIST', authPointItem) - 设置单个权限
     * 2. setAuthPointMap({ OPPORTUNITY_LIST: authPointItem }) - 浅合并对象
     * 3. setAuthPointMap(draft => { draft.OPPORTUNITY_LIST = authPointItem }) - 函数更新
     */
    setAuthPointMap: (data: any, value?: any) => {
      set((state) => {
        if (typeof data === 'string') {
          // 单个属性设置
          state.authPointMap[data as AuthPointType] = value
        } else if (typeof data === 'function') {
          // 函数方式
          data(state.authPointMap)
        } else if (typeof data === 'object' && data !== null) {
          // 对象方式 - 浅合并
          Object.assign(state.authPointMap, data)
        }
      })
    },

    /**
     * 设置用户(座席)列表数据
     * 支持 3 种调用方式：
     * 1. setStaffData('userId', staffInfo) - 设置单个用户
     * 2. setStaffData({ userId: staffInfo }) - 浅合并对象
     * 3. setStaffData(draft => { draft.userId = staffInfo }) - 函数更新
     */
    setStaffData: (data: any, value?: any) => {
      set((state) => {
        if (typeof data === 'string') {
          // 单个属性设置
          state.staffData[data] = value
        } else if (typeof data === 'function') {
          // 函数方式
          data(state.staffData)
        } else if (typeof data === 'object' && data !== null) {
          // 对象方式 - 浅合并
          Object.assign(state.staffData, data)
        }
      })
    },

    // ========== 重置方法 ==========
    /**
     * 重置所有状态到初始值
     */
    reset: () => {
      set(initialState)
    },
  }))
)
